import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";
import { conditionalSocket } from "../../types";

/**
 * Decision V4 operators. Binary operators compare `left` against `right`.
 * Unary operators (`is_empty`, `is_not_empty`, `is_true`, `is_false`) evaluate
 * only `left` and ignore `right` — matching {@link decisionStep}.
 */
export const DecisionV4OperatorSchema = z.enum([
  "equals",
  "not_equals",
  "greater_than",
  "less_than",
  "greater_than_or_equal",
  "less_than_or_equal",
  "contains",
  "in",
  "is_empty",
  "is_not_empty",
  "is_true",
  "is_false",
]);

export type DecisionV4Operator = z.infer<typeof DecisionV4OperatorSchema>;

/** Operators that evaluate only the `left` input and ignore `right`. */
export const DECISION_V4_UNARY_OPERATORS = [
  "is_empty",
  "is_not_empty",
  "is_true",
  "is_false",
] as const satisfies readonly DecisionV4Operator[];

export type DecisionV4UnaryOperator =
  (typeof DECISION_V4_UNARY_OPERATORS)[number];

/**
 * Decision V4 Step
 *
 * General-purpose conditional branching with a dynamic `left` operand and an
 * optional `right` operand. Evaluates `left <operator> right` (or a unary
 * check on `left`) and routes the `left` value to either the `matched` or
 * `unmatched` output branch.
 *
 * Mirrors {@link decisionStep} but the comparison value is wired at runtime
 * instead of being a static config value, and (per the V4 convention) the
 * business logic lives in a pure use case separate from the handler.
 *
 * Example workflow (binary):
 * ```
 * RandomNumber ─┐
 *               ├─→ DecisionV4(operator: greater_than)
 * Threshold ────┘     ├─ [matched]   → HelloWorld("Above")
 *                     └─ [unmatched] → HelloWorld("Below")
 * ```
 *
 * Example workflow (unary — `right` can stay unwired):
 * ```
 * ExtractedField → DecisionV4(operator: is_empty)
 *                    ├─ [matched]   → HandleMissing
 *                    └─ [unmatched] → Continue
 * ```
 *
 * Skip propagation (in the orchestration engine) marks the untaken branch as
 * skipped so the workflow completes correctly.
 */
export const decisionV4Step = defineStepConfig({
  type: "decision_v4",
  name: "Decision V4",
  description:
    "Conditional branching comparing two dynamic input values with an operator, or checking a single value for emptiness / truthiness",
  category: "Control Flow",

  inputSchema: z.object({
    left: z.any().describe("Left-hand operand to evaluate"),
    // Optional so unary operators (is_empty, etc.) can run without wiring
    // `right`. Binary operators still expect a value when the condition
    // depends on it.
    right: z
      .any()
      .optional()
      .describe(
        "Right-hand operand to compare against (ignored for unary operators)",
      ),
  }),

  outputSchema: z.object({
    matched: conditionalSocket(z.any())
      .optional()
      .describe("The `left` value when the condition is met"),
    unmatched: conditionalSocket(z.any())
      .optional()
      .describe("The `left` value when the condition is not met"),
  }),

  configSchema: z.object({
    operator: DecisionV4OperatorSchema.default("equals").describe(
      "The operator applied to the left input (and right, for binary operators)",
    ),
  }),

  emits: [] as const,
});
